import {createContext, useContext, useId, useLayoutEffect} from 'react';

type Report = (id: string, width: number | null) => void;

/**
 * Lets content inside the fused card's side panel report the narrowest width it fits in. Several
 * pieces can report; the panel's floor is the widest of them.
 */
export const PanelMinWidthContext = createContext<Report | null>(null);

/** Reports `width` (px) as one of the side panel's floors while mounted; `null` withdraws it. */
export const useReportPanelMinWidth = (width: number | null) => {
  const report = useContext(PanelMinWidthContext);
  const id = useId();
  useLayoutEffect(() => {
    report?.(id, width);
    return () => report?.(id, null);
  }, [report, id, width]);
};

/** The nearest scrolling ancestor, or the element itself when there is none. */
const scrollParentOf = (element: HTMLElement): HTMLElement => {
  let node = element.parentElement;
  while (node && node !== document.body) {
    if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return element;
};

/** Width the panel adds around `element`: its scroll container's margins, padding, and scrollbar. */
export const widthAround = (element: HTMLElement) =>
  Math.max(scrollParentOf(element).offsetWidth - element.offsetWidth, 0);
